import { api, unwrapList } from '../../../shared/lib/axios';
import type { User, Company, UserDocument, DocumentType } from '../../../shared/lib/api';

export const USUARIOS_KEYS = {
  all: ['usuarios'] as const,
  list: (params?: object) => ['usuarios', 'list', params] as const,
};

export const EMPRESAS_PARA_SELECT_KEYS = {
  all: ['empresas-select'] as const,
};

type CreateUsuario = Omit<User, 'id' | 'createdAt' | 'updatedAt'>;
type UpdateUsuario = {
  name: string;
  email: string;
  phone: string;
  companyId?: string;
  /**
   * Perfil do usuário. Obrigatório ao reativar alguém cujo perfil foi
   * desativado ou excluído — o backend recusa a reativação sem ele
   * (`users.service.ts`, `resolveRoleForUpdate`). A tela de usuários ainda
   * não expõe reativação; quando expuser, é aqui que o seletor entra.
   */
  roleId?: string;
};

interface ListParams {
  companyId?: string;
  role?: string;
  limit?: number;
  offset?: number;
}

interface ListResponse<T> {
  data: T[];
  pagination?: { limit: number; offset: number; count: number };
}

export async function listarUsuarios(params?: ListParams): Promise<ListResponse<User>> {
  const query = new URLSearchParams();
  if (params?.companyId) query.set('company_id', params.companyId);
  if (params?.role) query.set('role', params.role);
  if (params?.limit) query.set('limit', String(params.limit));
  if (params?.offset) query.set('offset', String(params.offset));
  const qs = query.toString();
  const { data } = await api.get<ListResponse<User>>(`/users${qs ? `?${qs}` : ''}`);
  return data;
}

export async function listarEmpresasParaSelect(): Promise<Company[]> {
  const { data } = await api.get<ListResponse<Company>>('/companies?isActive=true&limit=100');
  return data.data;
}

/** O envio do e-mail de boas-vindas é best-effort; a flag indica se ele saiu. */
/**
 * `passwordSetupLink` só vem quando o e-mail de boas-vindas falhou: é o link
 * de primeiro acesso para o admin repassar manualmente.
 */
export type UsuarioCriado = User & {
  welcomeEmailSent?: boolean;
  passwordSetupLink?: { url: string; expiresInHours: number } | null;
};

export async function criarUsuario(payload: CreateUsuario): Promise<UsuarioCriado> {
  const fixed = {
    ...payload,
    companyId: payload.companyId === '' ? null : payload.companyId,
    managerId: payload.managerId === '' ? null : payload.managerId,
    supplierId: payload.supplierId === '' ? null : payload.supplierId,
  };
  const { data } = await api.post<UsuarioCriado | { data: UsuarioCriado }>('/users', fixed);
  return 'data' in data ? data.data : data;
}

export async function atualizarUsuario(id: string, payload: UpdateUsuario): Promise<void> {
  await api.patch(`/users/${id}`, payload);
}

export async function atribuirGestor(collaboratorId: string, managerId: string): Promise<User> {
  const { data } = await api.patch<User>(`/users/${collaboratorId}/manager`, { managerId });
  return data;
}

export async function devolverColaborador(collaboratorId: string, motivo: string): Promise<void> {
  await api.post(`/users/${collaboratorId}/devolver`, { motivo });
}

export async function buscarMeuGestor(): Promise<{ manager: User | null }> {
  const { data } = await api.get<{ manager: User | null }>('/users/me/manager');
  return data;
}

export async function buscarMeusColaboradores(): Promise<User[]> {
  const { data } = await api.get<User[] | { data: User[] }>('/users/me/collaborators');
  return unwrapList(data);
}

export async function listarDocumentos(userId: string): Promise<UserDocument[]> {
  const { data } = await api.get<UserDocument[] | { data: UserDocument[] }>(`/users/${userId}/documents`);
  return unwrapList(data);
}

export async function criarDocumento(
  userId: string,
  payload: { documentType: DocumentType; file: File },
): Promise<UserDocument> {
  const formData = new FormData();
  formData.append('documentType', payload.documentType);
  formData.append('file', payload.file);
  const { data } = await api.post<UserDocument>(`/users/${userId}/documents`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
}

export async function deletarDocumento(userId: string, docId: string): Promise<void> {
  await api.delete(`/users/${userId}/documents/${docId}`);
}

/** Registro do histórico de salário; `validFrom` é a competência inicial (`YYYY-MM`). */
export interface SalarioColaborador {
  id: string;
  collaboratorId: string;
  amount: number;
  validFrom: string;
  createdAt: string;
  updatedAt: string;
}

export async function listarSalarios(collaboratorId: string): Promise<SalarioColaborador[]> {
  const { data } = await api.get<{ data: SalarioColaborador[] }>(`/collaborators/${collaboratorId}/salaries`);
  return data.data ?? [];
}

export async function criarSalario(
  collaboratorId: string,
  payload: { amount: number; validFrom: string },
): Promise<SalarioColaborador> {
  const { data } = await api.post<SalarioColaborador>(`/collaborators/${collaboratorId}/salaries`, payload);
  return data;
}

export async function atualizarSalario(
  collaboratorId: string,
  id: string,
  payload: { amount?: number; validFrom?: string },
): Promise<SalarioColaborador> {
  const { data } = await api.patch<SalarioColaborador>(`/collaborators/${collaboratorId}/salaries/${id}`, payload);
  return data;
}

export async function excluirSalario(collaboratorId: string, id: string): Promise<void> {
  await api.delete(`/collaborators/${collaboratorId}/salaries/${id}`);
}
